import React, { useState } from 'react';
import { AtSign, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { useMentionNavigation, MentionReference } from '@/hooks/useMentionNavigation';

interface MentionNavigatorProps {
  messages: any[];
  currentUsername?: string;
  onJumpToMention: (messageId: string) => void;
  isMobile?: boolean;
}

const MentionNavigator = ({
  messages,
  currentUsername,
  onJumpToMention,
  isMobile = false
}: MentionNavigatorProps) => {
  const { mentions, mentionCount, hasMentions } = useMentionNavigation(messages, currentUsername);
  const [isOpen, setIsOpen] = useState(false);

  if (!hasMentions) {
    return null;
  }

  const handleMentionClick = (mention: MentionReference) => {
    onJumpToMention(mention.messageId);
    setIsOpen(false);
  };

  const truncateMessage = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={isMobile ? "sm" : "default"}
          className="relative bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white"
        >
          <AtSign className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
          {!isMobile && <span className="mr-1">Mentions</span>}
          <Badge 
            variant="secondary" 
            className={`${isMobile ? 'text-xs px-1' : ''} bg-sports-green text-black font-bold ml-1`}
          >
            {mentionCount}
          </Badge>
          <ChevronDown className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ml-1`} />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        className="w-80 bg-gray-800 border-gray-700 text-white z-50"
        align="end"
      >
        <div className="p-3 border-b border-gray-700">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <AtSign className="h-4 w-4 text-sports-green" />
            Your Mentions ({mentionCount})
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Click to jump to message
          </p>
        </div>

        <ScrollArea className="max-h-96">
          <div className="p-2">
            {mentions.map((mention) => (
              <div
                key={mention.messageId}
                onClick={() => handleMentionClick(mention)}
                className="p-3 mb-2 rounded-lg bg-gray-700/50 hover:bg-gray-700 cursor-pointer transition-colors border border-gray-600 hover:border-sports-green/50"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-sports-green truncate">
                      @{mention.senderUsername}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatDistanceToNow(new Date(mention.timestamp), { addSuffix: true })}
                  </span>
                </div>
                
                <p className="text-sm text-gray-300 line-clamp-2 break-words">
                  {mention.messageText}
                </p>

                <div className="mt-2 flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    <AtSign className="h-3 w-3 mr-1" />
                    {currentUsername}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        {mentions.length === 0 && (
          <div className="p-6 text-center text-gray-400">
            <AtSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No mentions found</p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MentionNavigator;
